import {
  Activity,
  Bot,
  BriefcaseBusiness,
  Files,
  FolderTree,
  Gauge,
  Globe2,
  Home,
  PanelsTopLeft,
  Monitor,
  Settings,
  SquareTerminal,
  Trash2,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { lazy, type ComponentType } from "react";
import type { CompiledProgramSlug } from "../lib/programs";
const DesktopPage = lazy(() => import("../pages/home").then((module) => ({ default: module.DesktopPage })));
const FilesPage = lazy(() => import("../pages/files").then((module) => ({ default: module.FilesPage })));
const TrashPage = lazy(() => import("../pages/files").then((module) => ({ default: module.TrashPage })));
const UsersPage = lazy(() => import("../pages/users").then((module) => ({ default: module.UsersPage })));
const ActivityPage = lazy(() => import("../pages/activity").then((module) => ({ default: module.ActivityPage })));
const StatusPage = lazy(() => import("../pages/status").then((module) => ({ default: module.StatusPage })));
const SettingsPage = lazy(() => import("../pages/settings").then((module) => ({ default: module.SettingsPage })));

const HostFilesPage = lazy(() => import("../hostWorkspacePages").then((module) => ({ default: module.HostFilesPage })));
const HostTerminalPage = lazy(() => import("../terminalWorkspacePage").then((module) => ({ default: module.HostTerminalPage })));
const HostCodexPage = lazy(() => import("../codexWorkspacePage").then((module) => ({ default: module.HostCodexPage })));
const HostBrowserPage = lazy(() => import("../browserWorkspacePage").then((module) => ({ default: module.HostBrowserPage })));
const CrmPage = lazy(() => import("../crmPage").then((module) => ({ default: module.CrmPage })));
const WorkbenchPage = lazy(() => import("../workbench/WorkbenchPage").then((module) => ({ default: module.WorkbenchPage })));
const RemoteDesktopPage = lazy(() => import("../remoteDesktopPage").then((module) => ({ default: module.RemoteDesktopPage })));
const OperationsPage = lazy(() => import("../operations/OperationsPage").then((module) => ({ default: module.OperationsPage })));

export type DesktopAppId = CompiledProgramSlug;

export type DesktopAppDefinition = {
  id: DesktopAppId;
  path: string;
  title: string;
  description: string;
  icon: LucideIcon;
  component: ComponentType;
  adminOnly?: boolean;
};

export const desktopApps: readonly DesktopAppDefinition[] = [
  { id: "home", path: "/", title: "Meu servidor", description: "Visão geral do servidor", icon: Home, component: DesktopPage },
  { id: "files", path: "/arquivos", title: "Arquivos", description: "Documentos e materiais", icon: Files, component: FilesPage },
  { id: "trash", path: "/lixeira", title: "Lixeira", description: "Itens removidos", icon: Trash2, component: TrashPage },
  { id: "users", path: "/usuarios", title: "Usuários", description: "Pessoas e acessos", icon: Users, component: UsersPage, adminOnly: true },
  { id: "activity", path: "/atividade", title: "Atividade", description: "Histórico do ambiente", icon: Activity, component: ActivityPage },
  { id: "status", path: "/status", title: "Status", description: "Saúde dos serviços", icon: Gauge, component: StatusPage },
  { id: "settings", path: "/configuracoes", title: "Configurações", description: "Preferências da conta", icon: Settings, component: SettingsPage },
  { id: "host-files", path: "/projetos", title: "Explorador", description: "Pastas e projetos no servidor", icon: FolderTree, component: HostFilesPage },
  { id: "host-terminal", path: "/terminal", title: "Terminal", description: "Terminal administrativo do servidor", icon: SquareTerminal, component: HostTerminalPage },
  { id: "host-codex", path: "/codex", title: "Codex", description: "Assistente de desenvolvimento no servidor", icon: Bot, component: HostCodexPage },
  { id: "host-browser", path: "/navegador", title: "Navegador", description: "Navegadores dos projetos", icon: Globe2, component: HostBrowserPage },
  { id: "crm", path: "/crm", title: "CRM", description: "Clientes, oportunidades e operação comercial", icon: BriefcaseBusiness, component: CrmPage },
  { id: "workbench", path: "/central-projetos", title: "Central de Projetos", description: "Contexto, tarefas, materiais e decisões dos projetos", icon: PanelsTopLeft, component: WorkbenchPage },
  { id: "remote-desktop", path: "/servidor-ubuntu", title: "Servidor Ubuntu", description: "Acesso privado à área de trabalho do servidor", icon: Monitor, component: RemoteDesktopPage },
  { id: "operations", path: "/operacoes", title: "Operações", description: "Busca, processamento e tarefas persistentes do servidor", icon: Workflow, component: OperationsPage },
] as const;

export function appById(id: DesktopAppId) {
  return desktopApps.find((app) => app.id === id);
}

export function appByPath(pathname: string) {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return desktopApps.find((app) => app.path === normalized);
}
